import { useRef, useEffect, useState, useCallback } from 'react';

/**
 * Custom hook to execute a callback after the next update of a dependency
 * @param dependency The dependency to watch for changes
 * @returns A function to schedule the callback for next update
 */
export function useDeferredCallback<T extends any[]>(...dependencies: T) {
  // Store for pending callbacks and their arguments
  const pendingCallbackRef = useRef<Array<{
    callback: Function,
  }>>([]);

  // Track the dependency value to detect changes
  const [prevDependencies, setPrevDependencies] = useState<T>(dependencies);

  // Check for dependency updates and execute pending callbacks
  useEffect(() => {
    // If dependency changed and we have pending callbacks
    if (JSON.stringify(dependencies) !== JSON.stringify(prevDependencies) && pendingCallbackRef.current.length > 0) {
      // Execute all pending callbacks
      pendingCallbackRef.current.forEach(({ callback }) => {
        callback(...dependencies);
      });
      // Clear the pending callbacks
      pendingCallbackRef.current = [];
      // Update the tracked dependency value
      setPrevDependencies(dependencies);
    }
  }, [dependencies, prevDependencies]);

  // Function to schedule a callback for the next dependency update
  const deferCallback = useCallback((callback: (...args: T) => void) => {
    pendingCallbackRef.current.push({ callback });
  }, []);

  return deferCallback;
}
